import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Nuova Orchestra da Camera Ferruccio Busoni";
const BASE_URL = "https://www.orchestrabusoni.it";
const DEFAULT_IMAGE = "https://www.orchestrabusoni.it/img/social/og-home.jpg";

const SEO = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  type = "website",
  schema,
  breadcrumbs,
  keywords,
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const isHome = location.pathname === "/";
    const fullTitle = isHome
      ? (title ? `${title} | Trieste` : `${SITE_NAME} | Trieste`)
      : (title ? `${title} | ${SITE_NAME}` : SITE_NAME);

    document.title = fullTitle;

    const setMetaTag = (attributeName, attributeValue, contentValue) => {
      if (!contentValue) return;
      let el = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attributeName, attributeValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", contentValue);
    };

    const isEnglish = i18n.language && i18n.language.startsWith("en");
    const defaultDesc = isEnglish
      ? "Official website of the Nuova Orchestra da Camera Ferruccio Busoni of Trieste. Historical chamber orchestra founded in 1965 by Aldo Belli and directed by Massimo Belli."
      : "Sito ufficiale della Nuova Orchestra da Camera Ferruccio Busoni di Trieste. Complesso storico fondato nel 1965 da Aldo Belli e diretto da Massimo Belli. Concerti, discografia e Mattinate Musicali Internazionali.";

    const metaDesc = description || defaultDesc;
    const currentUrl = `${BASE_URL}${location.pathname}`;

    // Resolve image URL
    let fullImageUrl = DEFAULT_IMAGE;
    if (image) {
      if (image.startsWith("http://") || image.startsWith("https://")) {
        fullImageUrl = image;
      } else {
        fullImageUrl = `${BASE_URL}${image.startsWith("/") ? "" : "/"}${image}`;
      }
    }

    const imgType = fullImageUrl.endsWith(".png") ? "image/png" : "image/jpeg";

    // Standard Meta Tags
    setMetaTag("name", "title", fullTitle);
    setMetaTag("name", "description", metaDesc);
    if (keywords) {
      setMetaTag("name", "keywords", keywords);
    }
    setMetaTag("name", "author", SITE_NAME);
    setMetaTag("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // OpenGraph (Facebook, WhatsApp, Telegram, LinkedIn)
    setMetaTag("property", "og:site_name", SITE_NAME);
    setMetaTag("property", "og:type", type);
    setMetaTag("property", "og:url", currentUrl);
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", metaDesc);
    setMetaTag("property", "og:image", fullImageUrl);
    setMetaTag("property", "og:image:secure_url", fullImageUrl);
    setMetaTag("property", "og:image:type", imgType);
    setMetaTag("property", "og:image:width", "1200");
    setMetaTag("property", "og:image:height", "630");
    setMetaTag("property", "og:image:alt", fullTitle);
    setMetaTag("property", "og:locale", isEnglish ? "en_US" : "it_IT");
    setMetaTag("property", "og:locale:alternate", isEnglish ? "it_IT" : "en_US");

    // Twitter Card
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:url", currentUrl);
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", metaDesc);
    setMetaTag("name", "twitter:image", fullImageUrl);
    setMetaTag("name", "twitter:image:alt", fullTitle);

    // Canonical Link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", currentUrl);

    // Dynamic JSON-LD Schema (Combining Schema + Breadcrumbs)
    const schemaId = "dynamic-seo-schema";
    let scriptTag = document.getElementById(schemaId);

    const schemasToInject = [];
    if (schema) {
      if (Array.isArray(schema)) {
        schemasToInject.push(...schema);
      } else {
        schemasToInject.push(schema);
      }
    }

    if (breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((b, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": b.name,
          "item": b.url ? (b.url.startsWith("http") ? b.url : `${BASE_URL}${b.url}`) : undefined,
        })),
      };
      schemasToInject.push(breadcrumbSchema);
    }

    if (schemasToInject.length > 0) {
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.id = schemaId;
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }
      const combinedSchema = {
        "@context": "https://schema.org",
        "@graph": schemasToInject,
      };
      scriptTag.textContent = JSON.stringify(combinedSchema);
    } else if (scriptTag) {
      scriptTag.remove();
    }
  }, [title, description, image, type, schema, breadcrumbs, keywords, location.pathname, i18n.language]);

  return null;
};

export default SEO;
